import { Component, Input, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityService } from '../../core/services/activity.service';
import { NotificationService } from '../../core/services/notification.service';
import { ActivityItem } from '../../core/interfaces/activityItem.interface';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'app-activity-list',
    standalone: true,
    templateUrl: './activity-list.component.html',
    styleUrls: ['./activity-list.component.scss'],
    imports: [
        CommonModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatRadioModule,
        MatProgressSpinnerModule
    ]
})
export class ActivityListComponent {

    private activityService = inject(ActivityService);
    private notify = inject(NotificationService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    @Input() showViewAll = false;
    @Input() enableLoadMore = true;
    @Input() menu = false;
    @Input() triggerRef!: MatMenuTrigger;

    activities: ActivityItem[] = [];
    loading = true;

    pageSize = 20;
    pageNumber = 1;
    totalPages = 0;

    sortType: 'newest' | 'oldest' = 'newest';
    activityPage = 'UploadHistory';

    ngOnInit() {
        this.getUserActivities();
    }

    sortChanged(type: 'newest' | 'oldest') {
        if (type === this.sortType) {
            return;
        }
        this.sortType = type;
        this.pageNumber = 1;
        this.activities = [];
        this.getUserActivities();
    }

    getUserActivities() {
        this.loading = true;

        if (!this.menu && this.router.url.startsWith('/activity')) {
            const type = this.route.snapshot.queryParamMap.get('activityPage');
            if (type) {
                this.activityPage = type;
                this.activityService.setActivities(type);
            }
        } else {
            const currentUrl = this.router.url;
            if (currentUrl.includes('upload-history')) {
                this.activityPage = 'UploadHistory';
            } else if (currentUrl.includes('file-upload')) {
                this.activityPage = 'FileManagement';
            } else if (currentUrl.includes('users')) {
                this.activityPage = 'UserManagement';
            }
            else if (currentUrl.includes('profile')) {
                this.activityPage = 'UserProfile';
            }
        }

        const payload = {
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
            sortDirection: this.sortType === 'newest' ? 'DESC' : 'ASC',
            activityPage: this.activityPage
        };


        this.activityService.getActivities(payload).subscribe({
            next: (res) => {
                this.loading = false;

                if (res.isSuccess) {
                    const newData = res.data?.items ?? [];

                    if (this.pageNumber === 1) {
                        this.activities = newData;
                    } else {
                        this.activities = [...this.activities, ...newData];
                    }

                    this.totalPages = res.data?.totalPages ?? 0;
                    if (this.totalPages && this.pageNumber >= this.totalPages) {
                        this.enableLoadMore = false;
                    }

                    if(this.activities.length === 0) {
                        this.enableLoadMore = false;
                        this.showViewAll = false;
                    }
                }
            },
            error: (err) => {
                this.loading = false;
                this.enableLoadMore = false;
                this.showViewAll = false;
                this.notify.error(err?.error?.message ?? 'Failed to load activities');
            }
        });
    }

    loadMore() {
        this.pageNumber++;
        this.getUserActivities();
    }

    viewAllClicked() {
        this.triggerRef.closeMenu();
        this.router.navigate(['/activity'], {
            queryParams: { activityPage: this.activityPage }
        });
    }

    getTimeAgo(dateString?: string): string {
        if (!dateString) return "-";
        const past = new Date(
            dateString.endsWith("Z") ? dateString : dateString + "Z"
        );
        const now = this.getUtcNow();

        const seconds = Math.floor((now.getTime() - past.getTime()) / 1000);

        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(seconds / 3600);
        const days = Math.floor(seconds / 86400);
        const months = Math.floor(days / 30);
        const years = Math.floor(days / 365);

        if (seconds < 60) return "Just now";
        if (minutes < 60) return `${minutes} min ago`;
        if (hours < 24) return `${hours} hrs ago`;
        if (days < 30) return `${days} days ago`;
        if (months < 12) return `${months} months ago`;
        return `${years} years ago`;
    }

    private getUtcNow(): Date {
        const d = new Date();
        return new Date(Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds(),
            d.getUTCMilliseconds()
        ));
    }

    ngOnDestroy() {
        this.activityService.setActivities(null);
    }


}
